
import {
  Component,
  VModel,
  Prop,
  Ref,
  Vue,
  Watch,
} from 'nuxt-property-decorator'
import { saveAs } from 'file-saver'
import MicListener, { MicListenerStatus } from './MicListener.vue'
import WebPlayerInfoModal from './WebPlayerInfoModal.vue'

import { WebPlayerThemeUtil } from './utils/WebPlayerThemeUtil'
import {
  PlaybackStatus,
  PlayerDurationListener,
  PlayerProgressListener,
  PlayerStatusListener,
} from './utils/EngineService'
import { WebPlayerLogger } from './utils/WebPlayerLogger'
import { AudioQuality } from './model/AudioQuality'
import { GameEvent } from './model/GameEvent'
import { PlatformSettingsWeb } from '~/api'

const NO_TIME = '--:--'

/**
 * Handles audio playback scrubbing, pausing, playing.
 */
@Component
export default class WebPlayerControls extends Vue {
  @Ref('info-modal') infoModal!: WebPlayerInfoModal
  @Ref('mic-listener') micListener?: MicListener

  @Prop() platformSettings!: PlatformSettingsWeb
  @Prop() audioUrl?: string | null
  @Prop() audioQuality!: AudioQuality
  @Prop({ default: false }) audioLoading!: boolean
  @Prop({ default: null }) video!: GameEvent | null
  @Prop({ default: false }) videoEnded!: boolean
  @Prop({ default: false }) isFullScreen!: boolean
  @Prop({ default: false }) isFullScreenSupported!: boolean
  @Prop() playFromVideoFrame!: boolean

  @VModel() playStatus!: PlaybackStatus

  PlaybackStatus = PlaybackStatus
  AudioQuality = AudioQuality

  audioPlayer!: HTMLAudioElement
  audioVolume = 1
  audioProgress = 0
  timeLeft = NO_TIME

  awaitingPlay = false

  private onProgressChange?: PlayerProgressListener
  private onStateChange?: PlayerStatusListener
  private onDuration?: PlayerDurationListener

  private logger = new WebPlayerLogger(this.isDebug)

  get isDebug() {
    return !!this.$route.query.debug
  }

  updateProgressTime() {
    if (!this.audioPlayer) {
      this.timeLeft = NO_TIME
      return
    }

    if (isNaN(this.audioPlayer.duration) || this.audioPlayer.duration <= 0) {
      this.timeLeft = NO_TIME
      return
    }

    const timeLeft = this.formatTime(
      this.audioPlayer.duration - this.audioPlayer.currentTime
    )
    this.timeLeft = `${timeLeft}`
  }

  formatTime(time: number) {
    time = Math.round(Number(time))
    const floorPad = (amount: number): string =>
      Math.floor(amount).toString().padStart(2, '0')

    const hours = floorPad(time / 3600)
    const minutes = floorPad((time % 3600) / 60)
    const seconds = floorPad((time % 3600) % 60)

    return `${hours === '00' ? '' : hours + ':'}${minutes}:${seconds}`
  }

  @Watch('playStatus')
  handlePlayStatusChange() {
    this.onStateChange?.call(undefined, this.playStatus)
  }

  mounted() {
    this.initAudioPlayer()
    this.$emit('onInitialize')
  }

  // ** Audio Events

  playAudio() {
    if (!this.audioPlayer) this.initAudioPlayer()
    if (!this.audioUrl) {
      this.onDuration?.call(undefined, 0)
      return
    }

    if (!this.audioPlayer.paused) {
      this.pause()
    }
    this.audioPlayer.src = this.audioUrl
    this.replay()
  }

  onAudioPause(playStatus: PlaybackStatus) {
    this.playStatus = playStatus
    if (!this.micListener) return
    if (this.micListener?.status === MicListenerStatus.AUTO) {
      this.startListening()
    }
  }

  onAudioPlay() {
    this.playStatus = PlaybackStatus.PLAY
    if (!this.micListener) return
    if (this.micListener.status === MicListenerStatus.AUTO) {
      this.stopListening()
    }
  }

  onSpeechResult(input: string) {
    this.playStatus = PlaybackStatus.LOAD
    if (input === 'start') {
      this.$emit('start')
    } else {
      this.$emit('sendInputRaw', input)
    }
  }

  onToggleMicListenerStatus(status: MicListenerStatus) {
    switch (status) {
      case MicListenerStatus.AUTO:
        this.startListening()
        break
      case MicListenerStatus.MUTED:
        this.stopListening()
        break
      case MicListenerStatus.HEADPHONES:
        this.startListening()
        break
    }
  }

  startListening() {
    if (this.micListener) {
      if (
        this.micListener?.status === MicListenerStatus.AUTO ||
        this.micListener?.status === MicListenerStatus.HEADPHONES
      ) {
        this.micListener.acceptSTT = true
        this.micListener.listenForSpeech()
      }
    }
  }

  stopListening() {
    if (this.micListener) {
      this.micListener.acceptSTT = false
      this.micListener?.listener?.stop()
    }
  }

  seekChange(e: Event) {
    if (!this.audioPlayer.src) return 
    
    const target = e.target as HTMLInputElement
    const progress = parseInt(target.value)

    if (Number.isNaN(progress)) {
      return this.logger.log('SeekChange', `Invalid progress value: ${target.value}`)
    }

    const newTime = (this.audioPlayer.duration / 100) * progress
    if (!Number.isFinite(newTime)) {
      return this.logger.log('SeekChange', `Non-finite time calculated: ${newTime}, duration: ${this.audioPlayer.duration}, progress: ${progress}`)
    }


    this.audioPlayer.currentTime = newTime
    this.audioProgress = Math.round(progress)
    this.updateProgressTime()  
  }

  initAudioPlayer() {
    this.audioPlayer = new Audio()
    this.audioPlayer.volume = this.audioVolume

    this.logger.log('AudioEvent', 'Init')

    this.audioPlayer.addEventListener('timeupdate', () => {
      this.audioProgress = Math.round(
        (100 * this.audioPlayer.currentTime) / this.audioPlayer.duration
      )

      this.updateProgressTime()

      this.onProgressChange?.call(
        undefined,
        1000 * this.audioPlayer.currentTime
      )
    })

    this.audioPlayer.onplay = () => {
      this.logger.log('AudioEvent', 'Speaking')
      this.onAudioPlay()
    }

    this.audioPlayer.onpause = () => {
      this.logger.log('AudioEvent', 'Finished Speaking')

      let playStatus: PlaybackStatus

      if (this.audioProgress === 100) {
        playStatus = PlaybackStatus.END
      } else {
        playStatus = PlaybackStatus.PAUSE
      }
      this.onAudioPause(playStatus)
    }
  }

  initCallbacks(
    onProgress: PlayerProgressListener,
    onStatus: PlayerStatusListener,
    onDuration: PlayerDurationListener
  ) {
    this.onProgressChange = onProgress
    this.onStateChange = onStatus
    this.onDuration = onDuration
  }

  /**
   *
   * Handles the play/pause button click event on WebPlayerComponent
   * There we just trigger a variable change on clicl
   * Here we handle the actual play/pause action
   * depending on PlaybackStatus
   *
   */
  @Watch('playFromVideoFrame')
  handleAction() {
    if (this.audioLoading) return

    if (this.videoEnded) {
      this.$emit('playNextNode')
      return
    }

    switch (this.playStatus) {
      case PlaybackStatus.INIT:
        // If audio file is preloaded play it
        if (this.audioUrl) {
          this.play()
        } else this.$emit('start')
        break
      case PlaybackStatus.PAUSE:
        this.play()
        break
      case PlaybackStatus.PLAY:
        this.pause()
        break
      case PlaybackStatus.END:
        if (this.audioProgress !== 100) {
          this.play()
        } else this.replay()
        break
    }
  }

  async play() {
    if (this.awaitingPlay || (!this.audioPlayer.src && !this.audioUrl)) return

    this.awaitingPlay = true
    this.audioPlayer.src = this.audioPlayer.src ?? this.audioUrl

    try {
      await this.audioPlayer.play()
      this.onDuration?.call(undefined, 1000 * this.audioPlayer.duration)
    } finally {
      this.awaitingPlay = false
    }
  }

  pause() {
    if(this.awaitingPlay) {
     this.logger.log('AudioEvent', 'Pause skipped - awaiting play')
     return
   }

    this.audioPlayer.pause()
  }

  replay() {
    this.audioPlayer.currentTime = 0
    this.play()
  }

  showInfoModal() {
    this.infoModal.show()
  }

  downloadAudio() {
    if (this.audioUrl) {
      saveAs(this.audioUrl, `Audio - ${this.audioQuality}.mp3`)
    }
  }

  // UI Stuff
  get controlsClass() {
    return WebPlayerThemeUtil.controlsClass(this.platformSettings)
  }

  get headerStyle() {
    return WebPlayerThemeUtil.headerStyle(this.platformSettings)
  }
}
